import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Panel kanan overlay (pola redesign Stitch: routing rules, source detail).
 * Bedanya dengan Modal: konten tinggi penuh, bisa memuat form panjang /
 * tabel rule sambil halaman utama tetap terlihat di belakang backdrop.
 *
 * Shell bertingkat: header (judul + badge + aksi + satu-satunya tombol close),
 * body scrollable, footer opsional sticky — komponen pemakai TIDAK boleh
 * menambah header/sendiri agar tidak dobel.
 */
export function SideDrawer({
  open,
  onClose,
  title,
  subtitle,
  badge,
  actions,
  footer,
  flush,
  width = '42rem',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Chip status kecil di samping judul (mis. LIVE / PAUSED). */
  badge?: React.ReactNode;
  /** Tombol aksi di kanan header (mis. SIMULATE). */
  actions?: React.ReactNode;
  /** Strip sticky di bawah (status ringkas, tombol simpan). */
  footer?: React.ReactNode;
  /** Hilangkan padding body — untuk konten dengan toolbar/strip sendiri yang
   *  ingin menempel penuh ke tepi drawer. */
  flush?: boolean;
  /** Tailwind width class for the panel, e.g. "w-[70vw]". Default 42rem. */
  width?: string;
  children: React.ReactNode;
}) {
  // Esc untuk menutup — pola keyboard yang sama seperti modal umumnya.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Portal ke body — kalau dirender inline di dalam page yang memakai
  // `space-y-*`, backdrop + aside kena `> * + *` margin-top dan drawer
  // bergeser turun (plus stacking context ancestor bisa menutup fixed).
  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
      />
      <aside
        role="dialog"
        aria-label={title}
        className={`fixed right-0 top-0 z-50 h-screen ${width} max-w-[95vw] bg-bg-secondary border-l border-neon-cyan/30 shadow-neon-cyan flex flex-col animate-slide-in`}
      >
        {/* Header — satu-satunya; close tunggal di kanan */}
        <div className="px-5 py-3.5 border-b border-border-subtle flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="font-display text-base uppercase tracking-widest text-neon-cyan truncate">
                {title}
              </h2>
              {badge}
            </div>
            {subtitle && (
              <p className="text-xs font-mono text-text-tertiary mt-1 truncate">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {actions}
            <button
              type="button"
              onClick={onClose}
              className="text-text-tertiary hover:text-neon-red p-1.5 border border-transparent hover:border-neon-red/30 rounded transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto ${flush ? '' : 'p-5'}`}>{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-border-subtle bg-bg-elevated">
            {footer}
          </div>
        )}
      </aside>
    </>,
    document.body,
  );
}
